/**
 * MALER FOR ARRANGEMENT I EN SAL (QA15-S B6). Innholdet er konsertmalens form (`concertTemplateContentSchema`).
 *
 * TIDENE ER RELATIVE TIL DØRENE OG VISES SOM KLOKKESLETT. Malen har sine dører («20:00»); en vakt står som
 * minutter fra dørene, og skjermen viser «Get-in 4 t før dørene = 16:00». Brukes malen på et arrangement med
 * andre dører, flytter alt seg like mye, og forhåndsvisningen sier det før noe lagres (funn 30).
 *
 * Funksjonene under er rene, så forhåndsvisningen i nettleseren og API-et regner likt.
 */
import { z } from 'zod';

import { concertTemplateContentSchema, type ConcertTemplateContent } from './org-concerts.js';

export const MAX_VENUE_TEMPLATES_PER_ROOM = 30;

export const venueTemplateDtoSchema = z
  .object({
    id: z.uuid(),
    roomId: z.uuid(),
    layoutId: z.uuid().nullable(),
    layoutName: z.string().nullable(),
    name: z.string(),
    isDefault: z.boolean(),
    content: concertTemplateContentSchema,
    updatedAt: z.string(),
  })
  .meta({ id: 'VenueTemplate' });
export type VenueTemplateDto = z.infer<typeof venueTemplateDtoSchema>;

export const venueTemplateListSchema = z
  .object({ items: z.array(venueTemplateDtoSchema) })
  .meta({ id: 'VenueTemplateList' });
export type VenueTemplateList = z.infer<typeof venueTemplateListSchema>;

const templateName = z.string().trim().min(1).max(120);

export const createVenueTemplateRequestSchema = z
  .object({
    name: templateName,
    /** Oppsettet malen hører til. Utelatt eller null: en egen mal. */
    layoutId: z.uuid().nullish(),
    /** Start fra en annen mal i salen (vanligvis standarden). Utelatt: start tomt med standardens dører. */
    copyFromId: z.uuid().nullish(),
  })
  .meta({ id: 'CreateVenueTemplateRequest' });
export type CreateVenueTemplateRequest = z.infer<typeof createVenueTemplateRequestSchema>;

export const updateVenueTemplateRequestSchema = z
  .object({
    name: templateName.optional(),
    layoutId: z.uuid().nullish(),
    /** Gjør denne til salens standard. Den forrige slutter å være det. */
    isDefault: z.literal(true).optional(),
    content: concertTemplateContentSchema.optional(),
  })
  .meta({ id: 'UpdateVenueTemplateRequest' });
export type UpdateVenueTemplateRequest = z.infer<typeof updateVenueTemplateRequestSchema>;

/**
 * `POST /venue-nights/:id/apply-template`. `content` er linjene slik de ble godtatt i forhåndsvisningen
 * (endret eller fjernet); utelatt brukes malen som den er. `templateId` null er salens standard.
 */
export const applyVenueTemplateRequestSchema = z
  .object({
    templateId: z.uuid().nullable(),
    content: concertTemplateContentSchema.optional(),
  })
  .meta({ id: 'ApplyVenueTemplateRequest' });
export type ApplyVenueTemplateRequest = z.infer<typeof applyVenueTemplateRequestSchema>;

export const applyVenueTemplateResponseSchema = z
  .object({
    nightId: z.uuid(),
    templateId: z.uuid().nullable(),
    /** Satt når malen alt var brukt: da ble ingenting laget. */
    alreadyApplied: z.boolean(),
    shifts: z.number().int().nonnegative(),
    reservations: z.number().int().nonnegative(),
    tasks: z.number().int().nonnegative(),
  })
  .meta({ id: 'ApplyVenueTemplateResponse' });
export type ApplyVenueTemplateResponse = z.infer<typeof applyVenueTemplateResponseSchema>;

/* ------------------------------------------------------------------------------- tidene */

const DAY = 1440;

/** «HH:MM» → minutter etter midnatt. */
export function clockMinutes(clock: string): number {
  const [h, m] = clock.split(':');
  return Number(h) * 60 + Number(m);
}

/** Minutter etter midnatt (også negative eller over et døgn) → «HH:MM». */
export function minutesClock(minutes: number): string {
  const wrapped = ((minutes % DAY) + DAY) % DAY;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Klokkeslettet en linje får mot dørene: dører 20:00 og −240 er 16:00. */
export function clockAtOffset(doorsAt: string, offsetMinutes: number): string {
  return minutesClock(clockMinutes(doorsAt) + offsetMinutes);
}

/**
 * Starten som minutter fra dørene, ut fra et klokkeslett. Det meste av en kveld ligger fra morgenen samme dag
 * til noen timer etter dørene, så 08:00 med dører 20:00 er −720 (samme dag), og 01:00 er +300 (natta etter).
 */
export function startOffsetFor(doorsAt: string, clock: string): number {
  let diff = clockMinutes(clock) - clockMinutes(doorsAt);
  if (diff > 360) diff -= DAY;
  if (diff <= -1080) diff += DAY;
  return diff;
}

/** Slutten følger starten: 17:00–00:00 er sju timer, også over midnatt. Lik start og slutt er et døgn. */
export function endOffsetFor(doorsAt: string, startOffset: number, endClock: string): number {
  const startClock = clockMinutes(doorsAt) + startOffset;
  let span = (((clockMinutes(endClock) - startClock) % DAY) + DAY) % DAY;
  if (span === 0) span = DAY;
  return startOffset + span;
}

/** Hvor mye malen flytter seg på et arrangement: dører 20:00 i malen og 21:00 på kvelden er +60. */
export function doorsShift(templateDoors: string | null, nightDoors: string | null): number {
  if (templateDoors === null || nightDoors === null) return 0;
  let diff = clockMinutes(nightDoors) - clockMinutes(templateDoors);
  if (diff > 720) diff -= DAY;
  if (diff < -720) diff += DAY;
  return diff;
}

/** Et klokkeslett i malen (get-in, curfew) som minutter fra malens dører. */
export function clockOffsetFromDoors(doorsAt: string | null, clock: string | null): number | null {
  if (doorsAt === null || clock === null) return null;
  return startOffsetFor(doorsAt, clock);
}

/** Linjene som må ha en post eller en ressurs før malen kan lagres for en sal. */
export function venueTemplateProblems(content: ConcertTemplateContent): {
  postsWithoutRole: number;
  resourcesWithoutResource: number;
} {
  return {
    postsWithoutRole: content.posts.filter((post) => post.staffingRoleId == null).length,
    resourcesWithoutResource: content.resources.filter((row) => row.resourceId == null).length,
  };
}
